import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import ReferralCapture from "@/pages/ReferralCapture";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import ResetPassword from "@/pages/ResetPassword";
import LandingPage from "@/pages/LandingPage";
import AdminCardRequests from "@/pages/AdminCardRequests";
import AdminSettings from "@/pages/AdminSettings";
import Pricing from "@/pages/Pricing";
import AuthCallback from "@/pages/AuthCallback";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import SalesTerms from "@/pages/SalesTerms";
import Help from "@/pages/Help";
import Contact from "@/pages/Contact";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-1)]">
        <div className="text-[#6B655F] text-xs uppercase tracking-[0.25em] font-sans animate-pulse">
          Caricamento...
        </div>
      </div>
    );
  }
  return user ? children : <Navigate to="/" replace />;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-1)]">
        <div className="text-[var(--fg-3)] text-xs uppercase tracking-[0.25em] font-sans animate-pulse">
          Caricamento...
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Synchronously handle OAuth callback (URL fragment contains session_id)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingOrDashboard />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route path="/invita/:code" element={<ReferralCapture />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/r/:cardId" element={<LandingPage />} />
      <Route
        path="/admin/card-requests"
        element={
          <AdminRoute>
            <AdminCardRequests />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/settings/card-pricing"
        element={
          <AdminRoute>
            <AdminSettings />
          </AdminRoute>
        }
      />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/assistenza" element={<Help />} />
      <Route path="/contatti" element={<ProtectedRoute><Contact /></ProtectedRoute>} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/condizioni-vendita" element={<SalesTerms />} />
    </Routes>
  );
}

function LandingOrDashboard() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-1)]">
        <div className="text-[#6B655F] text-xs uppercase tracking-[0.25em] font-sans animate-pulse">
          Caricamento...
        </div>
      </div>
    );
  }
  return user ? <Navigate to="/dashboard" replace /> : <Login />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
